// Tarif dégressif VTC par seuils kilométriques MARGINAUX (`pro_resource_pricing_tier.distance_bands`).
// SOURCE UNIQUE backend (moteur de prix, validation de l'upsert, aperçu) et frontend
// (VehiclePricingDialog, blocage du Save) : aucun autre code ne doit recalculer un prix par seuils.
//
// Sémantique : les km situés avant le premier seuil sont au plein tarif ; au-delà de `from_km`,
// chaque km est facturé au taux actif × (100 − discount_pct) / 100, jusqu'au seuil suivant. Le
// prix est continu et croissant avec la distance : une course plus longue ne coûte jamais moins
// cher (contrairement à un taux unique choisi selon la distance totale).

import type { DistanceBandObject } from './professional';
import { RideDistanceBandsMode } from './translation_namespace/availabilities_namespace';

export const DISTANCE_BANDS_MAX = 3;
export const DISTANCE_BAND_MIN_KM = 1;
export const DISTANCE_BAND_DISCOUNT_MIN = 1;
export const DISTANCE_BAND_DISCOUNT_MAX = 99;

// Distances de l'aperçu affiché au pro (aller simple, hors frais).
export const DISTANCE_BANDS_PREVIEW_KM: readonly number[] = [50, 150, 300];
// Dernier seuil au-delà de la dernière distance d'aperçu : une ligne est ajoutée à seuil × 1,5,
// sinon aucune ligne ne montrerait l'effet de ce seuil (ex. seuil 300 km → ligne 450 km).
const DISTANCE_BANDS_PREVIEW_BEYOND_FACTOR = 1.5;
// Taux d'exemple (unités mineures / km) de l'aperçu de la grille GLOBALE, qui ne dépend d'aucun
// véhicule : l'aperçu d'un véhicule utilise son propre taux aller.
export const DISTANCE_BANDS_PREVIEW_EXAMPLE_RATE_MINOR = 200;

// Seuils proposés quand le pro crée une grille (globale ou propre à un véhicule) sans en avoir.
export const DISTANCE_BANDS_DEFAULT: readonly DistanceBandObject[] = [
  { from_km: 50, discount_pct: 15 },
  { from_km: 150, discount_pct: 30 },
];

const isBandInBounds = (band: DistanceBandObject): boolean =>
  Number.isInteger(band.from_km) &&
  band.from_km >= DISTANCE_BAND_MIN_KM &&
  Number.isInteger(band.discount_pct) &&
  band.discount_pct >= DISTANCE_BAND_DISCOUNT_MIN &&
  band.discount_pct <= DISTANCE_BAND_DISCOUNT_MAX;

/**
 * Lecture du JSON stocké (il peut aussi être écrit par des scripts) : seul l'ordre est toléré (tri
 * par `from_km`, clés en trop retirées). Une grille qui ne respecte pas les règles d'écriture
 * (areDistanceBandsValid : bornes, 3 seuils max, km ET réduction strictement croissants) est
 * IGNORÉE en entier → `[]` (tarif linéaire) : le moteur n'applique jamais une grille que l'écran
 * refuserait, et l'écran n'affiche jamais une grille qui bloquerait l'enregistrement.
 * Ne lève jamais d'exception.
 */
export function normalizeDistanceBands(raw: unknown): DistanceBandObject[] {
  if (!Array.isArray(raw)) return [];
  const bands: DistanceBandObject[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) return [];
    const { from_km, discount_pct } = entry as Record<string, unknown>;
    if (typeof from_km !== 'number' || typeof discount_pct !== 'number') return [];
    bands.push({ from_km, discount_pct });
  }
  const sorted = bands.sort((a, b): number => a.from_km - b.from_km);
  return areDistanceBandsValid(sorted) ? sorted : [];
}

/**
 * Distances de l'aperçu pour une grille : DISTANCE_BANDS_PREVIEW_KM, plus une ligne au-delà du
 * dernier seuil quand celui-ci atteint la dernière distance (sinon l'aperçu n'affiche que « plein
 * tarif » alors que la grille réduit bien les courses plus longues).
 */
export function getDistanceBandsPreviewKm(bands: unknown): number[] {
  const distances = [...DISTANCE_BANDS_PREVIEW_KM];
  const normalized = normalizeDistanceBands(bands);
  const lastBand = normalized[normalized.length - 1];
  const lastDistance = distances[distances.length - 1];
  if (lastBand && lastBand.from_km >= lastDistance) {
    distances.push(Math.round(lastBand.from_km * DISTANCE_BANDS_PREVIEW_BEYOND_FACTOR));
  }
  return distances;
}

/**
 * Mode dégressif d'un véhicule d'après sa colonne brute `pro_resource_pricing_tier.distance_bands` :
 * null/absent = INHERIT (grille de la config course) ; grille vide, illisible ou hors règles =
 * NONE ; sinon CUSTOM. Même lecture que le moteur (`tier ?? global`, puis normalizeDistanceBands).
 */
export function readVehicleDistanceBandsMode(raw: unknown): RideDistanceBandsMode {
  if (raw === null || raw === undefined) return RideDistanceBandsMode.INHERIT;
  return normalizeDistanceBands(raw).length > 0 ? RideDistanceBandsMode.CUSTOM : RideDistanceBandsMode.NONE;
}

/**
 * Validation STRICTE en écriture : au plus DISTANCE_BANDS_MAX entrées dans les bornes, `from_km`
 * ET `discount_pct` strictement croissants dans l'ordre reçu (la réduction augmente avec la
 * distance). `[]` est valide (tarif linéaire).
 */
export function areDistanceBandsValid(bands: readonly DistanceBandObject[]): boolean {
  if (bands.length > DISTANCE_BANDS_MAX) return false;
  return bands.every(
    (band, i): boolean =>
      isBandInBounds(band) &&
      (i === 0 || (band.from_km > bands[i - 1].from_km && band.discount_pct > bands[i - 1].discount_pct)),
  );
}

/**
 * Prix au km d'un trajet (unités mineures) avec seuils marginaux. `legs` = 2 pour un aller-retour :
 * les seuils s'appliquent PAR LEG, puis on multiplie. Calcul en flottant, UN SEUL arrondi final.
 * Sans seuil, identique au bit près à l'ancien `Math.round(distance × taux)`.
 */
export function computeBandedRoutePriceMinor(
  distanceKm: number,
  ratePerKmMinor: number,
  bands: unknown,
  legs = 1,
): number {
  const legKm = Number.isFinite(distanceKm) && distanceKm > 0 ? distanceKm : 0;
  const sorted = normalizeDistanceBands(bands);
  if (sorted.length === 0) return Math.round(legKm * ratePerKmMinor * legs);

  let legPrice = 0;
  let cursorKm = 0;
  let keptPct = 100;
  for (const band of sorted) {
    if (legKm <= band.from_km) break;
    // rate × pct / 100 plutôt que rate × (1 − pct/100) : évite les flottants inexacts (0.15…).
    legPrice += ((band.from_km - cursorKm) * ratePerKmMinor * keptPct) / 100;
    cursorKm = band.from_km;
    keptPct = 100 - band.discount_pct;
  }
  legPrice += ((legKm - cursorKm) * ratePerKmMinor * keptPct) / 100;
  return Math.round(legPrice * legs);
}
